/**
 * Pick a category, or coin a new one — the pattern ContactPicker uses for solicitors.
 *
 * A category was free text, and Postgres compares strings exactly, so "travel", "Travel" and
 * "TRAVEL" were three different categories: a filter on one silently missed the others and a
 * year-end total split the same spending across three lines. Nothing ever errored.
 *
 * Two things stop that here. The list offers what this practice ALREADY uses, so reuse is the
 * path of least effort; and "Create new category" is withheld whenever the typed text matches
 * an existing one ignoring case — type "travel" where "Travel" exists and you are offered
 * Travel, not a second spelling of it. Whatever is finally stored is normalised by the API
 * (normaliseCategory in @jurivoxa/shared), so the browser cannot disagree with the server.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { MagnifyingGlass, Plus, X, CaretDown } from '@phosphor-icons/react';
import { fetchActivityCategories } from '@/lib/activitiesApi';
import { normaliseCategory, categoriesMatch, CATEGORY_MAX_LENGTH } from '@jurivoxa/shared';

interface Props {
  entryType: 'time' | 'expense';
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}

export function CategoryPicker({ entryType, value, onChange, disabled }: Props) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // The outside-click listener is registered once; this keeps it pointed at the current closure.
  const commitAndCloseRef = useRef<() => void>(() => {});

  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) commitAndCloseRef.current();
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const { data: categories = [] } = useQuery({
    queryKey: ['activity-categories', entryType],
    queryFn: () => fetchActivityCategories(entryType),
    staleTime: 60_000,
  });

  commitAndCloseRef.current = () => commitAndClose();

  const trimmed = query.trim();
  const results = useMemo(() => {
    if (!trimmed) return categories;
    const q = trimmed.toLowerCase();
    return categories.filter((c) => c.toLowerCase().includes(q));
  }, [categories, trimmed]);

  // The guard that keeps one category to one spelling: never offer to create something that
  // already exists under different capitalisation.
  const showCreateOption =
    trimmed.length > 0 && !categories.some((c) => categoriesMatch(c, trimmed));

  const choose = (category: string) => {
    onChange(category);
    setQuery('');
    setOpen(false);
  };

  /**
   * Close the panel, keeping whatever was typed.
   *
   * Typing a category and going straight to "Log expense" used to discard it: the click closed
   * the panel and the text was never committed, so the expense saved with no category and
   * nothing said so. Typed text is an intention, not a draft — an existing category is matched
   * however it was capitalised, and anything else is kept as a new one.
   */
  const commitAndClose = () => {
    const typed = query.trim();
    if (typed) {
      const existing = categories.find((c) => categoriesMatch(c, typed));
      onChange(existing ?? normaliseCategory(typed));
    }
    setQuery('');
    setOpen(false);
  };

  return (
    <div ref={containerRef} className="relative">
      {/* Closed state reads as a dropdown, matching the Matter select above it — the list is the
          point, typing is the exception. The search box lives INSIDE the open panel so the
          control itself stays a dropdown rather than looking like a free-text field. */}
      <button
        type="button"
        onClick={() => { if (!disabled) { setOpen((o) => !o); setQuery(''); } }}
        disabled={disabled}
        className="flex w-full items-center justify-between gap-2 rounded-md border border-input bg-background px-3 py-2 text-left text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-brand-600 disabled:opacity-50"
      >
        <span className={value ? 'truncate' : 'truncate text-muted-foreground'}>
          {value || (entryType === 'time' ? 'Select a category' : 'Select a category')}
        </span>
        <span className="flex shrink-0 items-center gap-1">
          {value && !disabled && (
            <span
              role="button"
              tabIndex={-1}
              aria-label="Clear category"
              onClick={(e) => { e.stopPropagation(); onChange(''); setQuery(''); }}
              className="rounded p-0.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            >
              <X className="h-3.5 w-3.5" />
            </span>
          )}
          <CaretDown className="h-3.5 w-3.5 text-muted-foreground" />
        </span>
      </button>

      {open && !disabled && (
        <div className="absolute z-50 mt-1 w-full overflow-hidden rounded-md border border-border bg-card shadow-lg">
          <div className="relative border-b border-border">
            <MagnifyingGlass className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
            <input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter') { e.preventDefault(); commitAndClose(); }
                if (e.key === 'Escape') { setQuery(''); setOpen(false); }
              }}
              placeholder={entryType === 'time' ? 'Search or add — e.g. Drafting' : 'Search or add — e.g. Travel'}
              maxLength={CATEGORY_MAX_LENGTH}
              className="h-9 w-full bg-transparent pl-8 pr-3 text-sm focus:outline-none"
            />
          </div>

          <div className="max-h-48 overflow-auto">
            {results.length > 0 && (
              <ul>
                {results.map((c) => (
                  <li key={c}>
                    <button
                      type="button"
                      onClick={() => choose(c)}
                      className={`flex w-full items-center px-3 py-2 text-left text-sm hover:bg-muted/40 ${c === value ? 'font-semibold text-brand-700 dark:text-brand-300' : ''}`}
                    >
                      {c}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {showCreateOption && (
              <button
                type="button"
                onClick={() => choose(normaliseCategory(trimmed))}
                className="flex w-full items-center gap-2 border-t border-border px-3 py-2 text-left text-sm text-brand-700 dark:text-brand-300 hover:bg-brand-50 dark:hover:bg-brand-500/10"
              >
                <Plus className="h-3.5 w-3.5" />
                <span>
                  Create new category: <span className="font-semibold">"{normaliseCategory(trimmed)}"</span>
                </span>
              </button>
            )}
            {results.length === 0 && !showCreateOption && (
              <div className="px-3 py-2 text-xs text-muted-foreground">
                {categories.length === 0 ? 'No categories yet — type to add the first one.' : 'No matches.'}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
